import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { Consumption as ConsumptionType, Resin, Material, Receipt, Disposal } from '../types';
import {
  consumptionAPI,
  resinsAPI,
  materialsAPI,
  receiptsAPI,
  disposalsAPI,
  logisticsBulkAPI
} from '../services/api';
import { formatDate, getTodayDateString } from '../utils/shiftUtils';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';

const shiftLabelShort = (shift: ConsumptionType['shift']) => {
  if (shift === 'first') return 'I smena';
  if (shift === 'second') return 'II smena';
  return 'III smena';
};

// Backend `/inventory` returns `materialId` populated (object) while frontend types expect a string.
// Normalize to a stable string id so selection/cards don't break after refresh.
const normalizeMaterialId = (v: any): string => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    if (typeof v._id === 'string') return v._id;
    if (typeof v.id === 'string') return v.id;
  }
  return '';
};

const shiftOptions = [
  { label: 'I smena', value: 'first' },
  { label: 'II smena', value: 'second' },
  { label: 'III smena', value: 'third' }
];

const formatKgSmart = (v: number) => {
  const rounded = Math.round(v);
  if (Math.abs(v - rounded) < 1e-9) return String(rounded);
  return v.toFixed(2);
};

const parseTimeToMinutes = (time: string): number | null => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours * 60 + minutes;
};

const isTimeInRange = (
  timeMinutes: number,
  fromMinutes: number | null,
  toMinutes: number | null
): boolean => {
  if (fromMinutes === null && toMinutes === null) return true;
  if (fromMinutes !== null && toMinutes !== null) {
    // Normal raspon (npr. 06:00-14:00)
    if (fromMinutes <= toMinutes) {
      return timeMinutes >= fromMinutes && timeMinutes <= toMinutes;
    }
    // Raspon preko ponoći (npr. 22:00-06:00)
    return timeMinutes >= fromMinutes || timeMinutes <= toMinutes;
  }
  if (fromMinutes !== null) {
    return timeMinutes >= fromMinutes;
  }
  // toMinutes !== null
  return toMinutes !== null ? timeMinutes <= toMinutes : true;
};

const isTimeInShift = (time: string, shift: 'first' | 'second' | 'third'): boolean => {
  const mins = parseTimeToMinutes(time);
  if (mins === null) return false;
  if (shift === 'first') {
    // 06:00 - 14:00
    return mins >= 6 * 60 && mins <= 14 * 60;
  }
  if (shift === 'second') {
    // 14:00 - 22:00
    return mins >= 14 * 60 && mins <= 22 * 60;
  }
  // third shift: 22:00 - 06:00 (preko ponoći)
  return mins >= 22 * 60 || mins <= 6 * 60;
};

const parseSemicolonTimes = (raw: string): string[] => {
  return raw
    .split(';')
    .map((t) => t.trim())
    .filter(Boolean);
};

const Consumption: React.FC = () => {
  const { user } = useAuth();
  const canBulkClear = user?.role === 'admin' || user?.role === 'logistika';

  const [consumptions, setConsumptions] = useState<ConsumptionType[]>([]);
  const [archivedConsumptions, setArchivedConsumptions] = useState<ConsumptionType[]>([]);
  const [resins, setResins] = useState<Resin[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [bulkClearStep, setBulkClearStep] = useState<0 | 1 | 2>(0);
  const [bulkConfirmText, setBulkConfirmText] = useState('');
  const [bulkClearLoading, setBulkClearLoading] = useState(false);

  const [materialReceipts, setMaterialReceipts] = useState<Receipt[]>([]);
  const [materialDisposals, setMaterialDisposals] = useState<Disposal[]>([]);

  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConsumption, setEditingConsumption] = useState<ConsumptionType | null>(null);

  const [filterFrom, setFilterFrom] = useState<Date | null>(null);
  const [filterTo, setFilterTo] = useState<Date | null>(null);
  const [selectedMaterialId, setSelectedMaterialId] = useState<string>('');
  const [selectedShifts, setSelectedShifts] = useState<Array<'first' | 'second' | 'third'>>([]);
  const [filterTimeFrom, setFilterTimeFrom] = useState('');
  const [filterTimeTo, setFilterTimeTo] = useState('');

  const [formData, setFormData] = useState({
    date: new Date(getTodayDateString()),
    shift: 'first' as 'first' | 'second' | 'third',
    time: '',
    times: [] as string[],
    employeeName: '',
    resinId: '',
    usageCount: 1
  });
  const [batchEntries, setBatchEntries] = useState<
    Array<{
      resinId: string;
      employeeName: string;
      times: string[];
    }>
  >([]);
  const [error, setError] = useState('');

  // Popup za detaljan prikaz vremena / zapisa po šarži
  const [timeDetailsVisible, setTimeDetailsVisible] = useState(false);
  const [timeDetailsBase, setTimeDetailsBase] = useState<ConsumptionType | null>(null);

  // Opseg datuma za istorijsko stanje (kartica "stanje na datum")
  const [historyRange, setHistoryRange] = useState<Date[] | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [consumptionData, archivedData, resinsData, materialsData] = await Promise.all([
        consumptionAPI.getAll(),
        consumptionAPI.getArchived(),
        resinsAPI.getAll(),
        materialsAPI.getAll()
      ]);

      setConsumptions(consumptionData);
      setArchivedConsumptions(archivedData || []);
      setResins(resinsData);
      setMaterials(materialsData);

      // Ensure we always have a valid selected material id that exists in materials list.
      const materialIds = (materialsData || []).map((m) => m._id).filter(Boolean);
      const currentId = normalizeMaterialId(selectedMaterialId as any);
      const nextId = currentId && materialIds.includes(currentId) ? currentId : (materialIds[0] || '');
      if (nextId !== currentId) setSelectedMaterialId(nextId);
    } catch (e) {
      console.error('Error fetching data:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kada promenimo izabrani materijal, povuci prijemnice i rashode za taj materijal
  useEffect(() => {
    const loadMovementForMaterial = async () => {
      if (!selectedMaterialId) {
        setMaterialReceipts([]);
        setMaterialDisposals([]);
        return;
      }
      try {
        const [receipts, disposals] = await Promise.all([
          receiptsAPI.getByMaterialId(selectedMaterialId),
          disposalsAPI.getByMaterialId(selectedMaterialId)
        ]);
        setMaterialReceipts(receipts || []);
        setMaterialDisposals(disposals || []);
      } catch (e) {
        console.error('Error fetching receipts/disposals for material', e);
      }
    };

    loadMovementForMaterial();
  }, [selectedMaterialId]);

  const resinOptions = useMemo(() => {
    return resins.map((r) => ({ label: `${r.name} (${r.weight}kg)`, value: r._id }));
  }, [resins]);

  const materialOptions = useMemo(() => {
    return materials.map((m) => ({ label: m.name, value: m._id }));
  }, [materials]);

  const rangeActive = !!(filterFrom && filterTo);
  const todayYmd = getTodayDateString();

  const filteredForTable = useMemo(() => {
    let list = consumptions;

    if (selectedMaterialId) {
      list = list.filter((c) => c.materialId === selectedMaterialId);
    }

    if (selectedShifts && selectedShifts.length > 0) {
      list = list.filter((c) => selectedShifts.includes(c.shift));
    }

    if (filterFrom || filterTo) {
      const from = filterFrom ? dayjs(filterFrom).startOf('day') : null;
      const to = filterTo ? dayjs(filterTo).endOf('day') : null;
      list = list.filter((c) => {
        const d = dayjs(c.date, 'YYYY-MM-DD');
        if (from && d.isBefore(from)) return false;
        if (to && d.isAfter(to)) return false;
        return true;
      });
    }

    if (filterTimeFrom || filterTimeTo) {
      const fromMinutes = filterTimeFrom.trim() ? parseTimeToMinutes(filterTimeFrom.trim()) : null;
      const toMinutes = filterTimeTo.trim() ? parseTimeToMinutes(filterTimeTo.trim()) : null;
      list = list.filter((c) => {
        if (!c.time) return false;
        const mins = parseTimeToMinutes(c.time);
        if (mins === null) return false;
        return isTimeInRange(mins, fromMinutes, toMinutes);
      });
    }

    return list;
  }, [consumptions, selectedMaterialId, selectedShifts, filterFrom, filterTo, filterTimeFrom, filterTimeTo]);

  // Filtrirana lista za ARHIVU (samo po materijalu – ostali filteri su u istorijskoj kartici)
  const filteredArchivedForTable = useMemo(() => {
    let list = archivedConsumptions;

    if (selectedMaterialId) {
      list = list.filter((c) => c.materialId === selectedMaterialId);
    }

    return list;
  }, [archivedConsumptions, selectedMaterialId]);

  const selectedInv = useMemo(() => {
    return materials.find((m) => m._id === selectedMaterialId) || null;
  }, [materials, selectedMaterialId]);

  // Svi zapisi za trenutno otvoren "view more" popup (po šarži, datumu i smeni)
  const timeDetailsItems = useMemo(() => {
    if (!timeDetailsBase) return [];
    const all = [...consumptions, ...archivedConsumptions];
    return all.filter(
      (c) =>
        c.resinId === timeDetailsBase.resinId &&
        c.date === timeDetailsBase.date &&
        c.shift === timeDetailsBase.shift
    );
  }, [timeDetailsBase, consumptions, archivedConsumptions]);

  const timeDetailsTotals = useMemo(() => {
    const totalUsage = timeDetailsItems.reduce((sum, c) => sum + (c.usageCount || 0), 0);
    const totalKg = timeDetailsItems.reduce((sum, c) => sum + (c.totalConsumption || 0), 0);
    return { totalUsage, totalKg };
  }, [timeDetailsItems]);

  const cardFrom = rangeActive && filterFrom ? dayjs(filterFrom).startOf('day') : dayjs(todayYmd).startOf('day');
  const cardTo = rangeActive && filterTo ? dayjs(filterTo).endOf('day') : dayjs(todayYmd).endOf('day');

  const cardConsumptions = useMemo(() => {
    if (!selectedMaterialId) return [];
    const fromMinutes = filterTimeFrom.trim() ? parseTimeToMinutes(filterTimeFrom.trim()) : null;
    const toMinutes = filterTimeTo.trim() ? parseTimeToMinutes(filterTimeTo.trim()) : null;
    return consumptions.filter((c) => {
      if (c.materialId !== selectedMaterialId) return false;
      if (selectedShifts.length > 0 && !selectedShifts.includes(c.shift)) return false;
      if ((fromMinutes !== null || toMinutes !== null) && c.time) {
        const mins = parseTimeToMinutes(c.time);
        if (mins === null || !isTimeInRange(mins, fromMinutes, toMinutes)) return false;
      } else if (fromMinutes !== null || toMinutes !== null) {
        // Ako je uključen filter po vremenu, ali zapis nema vreme -> isključi ga iz agregata
        return false;
      }
      const d = dayjs(c.date, 'YYYY-MM-DD');
      return d.valueOf() >= cardFrom.valueOf() && d.valueOf() <= cardTo.valueOf();
    });
  }, [consumptions, selectedMaterialId, selectedShifts, cardFrom, cardTo, filterTimeFrom, filterTimeTo]);

  const cardTotalKg = useMemo(() => {
    return cardConsumptions.reduce((sum, c) => sum + (c.totalConsumption || 0), 0);
  }, [cardConsumptions]);

  // Procena dostupne količine na početku izabranog perioda:
  // trenutno stanje + ukupno potrošeno u periodu (bez uračunavanja prijema/rashoda u periodu).
  const periodStartAvailable = useMemo(() => {
    if (!selectedInv) return 0;
    const current = selectedInv.availableWeight || 0;
    return current + cardTotalKg;
  }, [selectedInv, cardTotalKg]);

  const perShift = useMemo(() => {
    const out = { first: 0, second: 0, third: 0 } as Record<'first' | 'second' | 'third', number>;
    cardConsumptions.forEach((c) => {
      out[c.shift] += c.totalConsumption || 0;
    });
    return out;
  }, [cardConsumptions]);

  // Izračunaj istorijsko stanje do početka prve smene zadatog dana
  // (tj. sve pokrete NA i POSLE tog dana tretiramo kao "budućnost")
  const computeAvailableOnDate = (target: dayjs.Dayjs): number | null => {
    if (!selectedInv || !selectedMaterialId) return null;

    // Granični trenutak: početak dana krajnje selektovanog datuma
    const snapCut = target.startOf('day');

    const allConsumptionsForMaterial = [...consumptions, ...archivedConsumptions].filter(
      (c) => c.materialId === selectedMaterialId
    );

    const futureConsumptionsKg = allConsumptionsForMaterial
      .filter((c) => {
        const d = dayjs(c.date, 'YYYY-MM-DD');
        // Sve što je NA ili POSLE graničnog datuma je "budućnost"
        return !d.isBefore(snapCut);
      })
      .reduce((sum, c) => sum + (c.totalConsumption || 0), 0);

    const futureReceiptsQty = materialReceipts
      .filter((r) => {
        const d = dayjs(r.receiptDate);
        return !d.isBefore(snapCut);
      })
      .reduce((sum, r) => sum + (r.quantity || 0), 0);

    const futureDisposalsQty = materialDisposals
      .filter((d) => {
        const date = dayjs(d.disposalDate);
        return !date.isBefore(snapCut);
      })
      .reduce((sum, x) => sum + (x.quantity || 0), 0);

    const currentAvailable = selectedInv.availableWeight || 0;
    return currentAvailable - futureReceiptsQty + futureConsumptionsKg + futureDisposalsQty;
  };

  // Istorijsko stanje za opseg datuma (početak/kraj opsega)
  const historySnapshots = useMemo(() => {
    if (!historyRange || historyRange.length === 0) return null;

    const startDate = historyRange[0];
    const endDate = historyRange[1] || historyRange[0];

    const availableStart = computeAvailableOnDate(dayjs(startDate));
    const availableEnd = computeAvailableOnDate(dayjs(endDate));

    if (availableStart === null || availableEnd === null) return null;

    return {
      startDate,
      endDate,
      availableStart,
      availableEnd,
      delta: availableEnd - availableStart
    };
  }, [historyRange, selectedInv, selectedMaterialId, consumptions, archivedConsumptions, materialReceipts, materialDisposals]);

  const clearPeriod = () => {
    setFilterFrom(null);
    setFilterTo(null);
    setFilterTimeFrom('');
    setFilterTimeTo('');
  };

  const handleAdd = () => {
    setEditingConsumption(null);
    setError('');
    setFormData({
      date: new Date(getTodayDateString()),
      shift: 'first',
      time: '',
      times: [],
      employeeName: '',
      resinId: '',
      usageCount: 1
    });
    setBatchEntries([]);
    setModalVisible(true);
  };

  const handleEdit = (consumption: ConsumptionType) => {
    setEditingConsumption(consumption);
    setError('');
    const existingTimes =
      consumption.times && consumption.times.length > 0
        ? consumption.times
        : consumption.time
        ? [consumption.time]
        : [];
    setFormData({
      date: new Date(consumption.date),
      shift: consumption.shift,
      time: '',
      times: existingTimes,
      employeeName: consumption.employeeName,
      resinId: consumption.resinId,
      usageCount: existingTimes.length || consumption.usageCount || 1
    });
    setBatchEntries([]);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await consumptionAPI.delete(id);
      fetchData();
    } catch (e: any) {
      console.error('Error deleting consumption:', e);
      setError(e?.response?.data?.message || 'Greška pri brisanju zapisa potrošnje');
    }
  };

  const handleSubmit = async () => {
    try {
      setError('');
      if (editingConsumption) {
        // Edit postojećeg zapisa – ponašanje ostaje kao ranije (jedna šarža).
        if (!formData.employeeName.trim()) {
          setError('Zaposleni je obavezan.');
          return;
        }
        if (!formData.resinId) {
          setError('Šarža je obavezna.');
          return;
        }

        if (!formData.times || formData.times.length === 0) {
          setError('Potrebno je da dodate bar jedno vreme šarže.');
          return;
        }

        const resin = resins.find((r) => r._id === formData.resinId);
        if (!resin) {
          setError('Nepostojeća šarža.');
          return;
        }

        const usageCount = formData.times.length;

        const payload: Partial<ConsumptionType> = {
          date: dayjs(formData.date).format('YYYY-MM-DD'),
          shift: formData.shift,
          times: formData.times,
          time: formData.times[0],
          usageCount,
          employeeName: formData.employeeName.trim(),
          resinId: resin._id,
          resinName: resin.name,
          materialId: resin.materialId,
          materialName: resin.materialName,
          resinWeight: resin.weight,
          totalConsumption: resin.weight * usageCount
        };

        await consumptionAPI.update(editingConsumption._id, payload);
      } else {
        // Novi unos – podržava više šarži u istoj smeni / datumu.
        if (batchEntries.length === 0) {
          setError('Dodajte bar jedan zapis (šarža + vremena + zaposleni) u listu pre čuvanja.');
          return;
        }

        const dateStr = dayjs(formData.date).format('YYYY-MM-DD');
        const shift = formData.shift;

        // Validacija svih stavki u listi pre slanja.
        for (const entry of batchEntries) {
          if (!entry.resinId) {
            setError('Sve stavke u listi moraju imati izabranu šaržu.');
            return;
          }

          if (!entry.employeeName.trim()) {
            setError('Sva polja "Zaposleni" u listi moraju biti popunjena.');
            return;
          }
          if (!entry.times || entry.times.length === 0) {
            setError('Svaki zapis u listi mora imati bar jedno vreme.');
            return;
          }
          const resin = resins.find((r) => r._id === entry.resinId);
          if (!resin) {
            setError('Jedna od šarži u listi više ne postoji.');
            return;
          }
        }

        const requests = batchEntries.map((entry) => {
          const resin = resins.find((r) => r._id === entry.resinId)!;
          const usageCount = entry.times.length;

          const payload: Partial<ConsumptionType> = {
            date: dateStr,
            shift,
            times: entry.times,
            time: entry.times[0],
            usageCount,
            employeeName: entry.employeeName.trim(),
            resinId: resin._id,
            resinName: resin.name,
            materialId: resin.materialId,
            materialName: resin.materialName,
            resinWeight: resin.weight,
            totalConsumption: resin.weight * usageCount
          };

          return consumptionAPI.create(payload);
        });

        await Promise.all(requests);
      }

      setModalVisible(false);
      fetchData();
    } catch (e: any) {
      console.error('Error saving consumption:', e);
      setError(e?.response?.data?.message || 'Greška pri čuvanju zapisa');
    }
  };

  const actionsBodyTemplate = (rowData: ConsumptionType) => {
    return (
      <div className="flex gap-2">
        <Button icon="pi pi-pencil" severity="info" className="p-button-sm" onClick={() => handleEdit(rowData)} />
        <Button
          icon="pi pi-trash"
          severity="danger"
          className="p-button-sm"
          onClick={() => {
            confirmDialog({
              message: 'Da li ste sigurni da želite da obrišete ovaj zapis?',
              header: 'Potvrda brisanja',
              icon: 'pi pi-exclamation-triangle',
              accept: () => handleDelete(rowData._id)
            });
          }}
        />
      </div>
    );
  };

  return (
    <div className="p-4">
      <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h2 className="m-0">Potrošnja</h2>

        <div className="flex align-items-center gap-2 flex-wrap">
          <Button label="Dodaj potrošnju" icon="pi pi-plus" severity="success" onClick={handleAdd} />
          <Button label="Osveži" icon="pi pi-refresh" severity="secondary" onClick={fetchData} />
          <Button
            label="Arhiviraj trenutne zapise"
            icon="pi pi-box"
            severity="warning"
            className="p-button-outlined"
            onClick={() => {
              confirmDialog({
                message:
                  'Ovo će arhivirati sve postojeće zapise potrošnje za ovu sekciju. Stanje materijala ostaje isto. Nastaviti?',
                header: 'Arhiviraj potrošnju',
                icon: 'pi pi-exclamation-triangle',
                acceptClassName: 'p-button-warning',
                accept: async () => {
                  try {
                    setError('');
                    await consumptionAPI.archiveAll();
                    fetchData();
                  } catch (e: any) {
                    console.error('Error archiving consumptions:', e);
                    setError(
                      e?.response?.data?.message ||
                        'Greška pri arhiviranju zapisa potrošnje'
                    );
                  }
                }
              });
            }}
          />

          {canBulkClear && (
            <Button
              label="Obriši sve podatke"
              icon="pi pi-trash"
              severity="danger"
              onClick={() => {
                setBulkConfirmText('');
                setBulkClearStep(1);
              }}
              tooltip="Briše sve potrošnje (i arhivirane), prijemnice i rashodovanja"
              tooltipOptions={{ position: 'bottom' }}
            />
          )}

          <Dropdown
            value={selectedMaterialId || null}
            options={materialOptions}
            onChange={(e) => setSelectedMaterialId(e.value || '')}
            placeholder="Filtriraj po materijalu"
            showClear
            appendTo="self"
            style={{ minWidth: 260 }}
          />
        </div>
      </div>

      <TabView>
        <TabPanel header="Aktivno">
          <Card>
            <div className="flex align-items-center gap-3 flex-wrap">
              <div className="font-semibold">Filtriraj period:</div>
              <Calendar
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.value as Date)}
                dateFormat="dd.mm.yy"
                showIcon
                placeholder="Od"
                appendTo="self"
              />
              <span className="text-600">-</span>
              <Calendar
                value={filterTo}
                onChange={(e) => setFilterTo(e.value as Date)}
                dateFormat="dd.mm.yy"
                showIcon
                placeholder="Do"
                appendTo="self"
              />
              {(filterFrom || filterTo) && (
                <Button icon="pi pi-times" className="p-button-text" onClick={clearPeriod} aria-label="Reset perioda" />
              )}
              <MultiSelect
                value={selectedShifts}
                options={shiftOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setSelectedShifts(e.value || [])}
                placeholder="Smena(e)"
                display="chip"
                style={{ minWidth: 220 }}
              />
              <InputText
                value={filterTimeFrom}
                onChange={(e) => setFilterTimeFrom(e.target.value)}
                placeholder="Vreme od (HH:mm)"
                style={{ width: 130 }}
              />
              <InputText
                value={filterTimeTo}
                onChange={(e) => setFilterTimeTo(e.target.value)}
                placeholder="Vreme do (HH:mm)"
                style={{ width: 130 }}
              />
            </div>

            <div className="grid mt-3">
              <div className="col-12 md:col-4 lg:col-3">
                <Card className="h-full">
                  {!selectedInv ? (
                    <div className="text-600">Izaberite materijal</div>
                  ) : (
                    <div>
                      <div className="text-center" style={{ fontWeight: 700, fontSize: 18 }}>
                        {selectedInv.name}
                      </div>

                      <div className="text-center text-600 mt-3">Trenutno stanje</div>
                      <div className="text-center" style={{ fontWeight: 800, fontSize: 24 }}>
                        {formatKgSmart(selectedInv.availableWeight)} {selectedInv.unit}
                      </div>

                      <hr className="my-3" />

                      <div className="text-center text-600">Ukupno potrošeno</div>
                      <div className="text-center" style={{ fontWeight: 800, fontSize: 22 }}>
                        {formatKgSmart(cardTotalKg)} kg
                      </div>
                      <div className="text-center text-600" style={{ fontSize: 12 }}>
                        {rangeActive && filterFrom && filterTo ? `${formatDate(filterFrom)} - ${formatDate(filterTo)}` : 'Danas'}
                      </div>

                      {rangeActive && filterFrom && filterTo && (
                        <>
                          <hr className="my-3" />
                          <div className="text-center text-600">Stanje na početku perioda</div>
                          <div className="text-center" style={{ fontWeight: 800, fontSize: 20 }}>
                            {formatKgSmart(periodStartAvailable)} {selectedInv.unit}
                          </div>
                        </>
                      )}

                      {rangeActive && (
                        <>
                          <hr className="my-3" />
                          <div className="text-center text-600 mb-2" style={{ fontSize: 12 }}>
                            Po smenama:
                          </div>
                          <div className="flex justify-content-between text-700" style={{ fontSize: 13 }}>
                            <div>I smena</div>
                            <div>{formatKgSmart(perShift.first)} kg</div>
                          </div>
                          <div className="flex justify-content-between text-700" style={{ fontSize: 13 }}>
                            <div>II smena</div>
                            <div>{formatKgSmart(perShift.second)} kg</div>
                          </div>
                          <div className="flex justify-content-between text-700" style={{ fontSize: 13 }}>
                            <div>III smena</div>
                            <div>{formatKgSmart(perShift.third)} kg</div>
                          </div>
                        </>
                      )}

                      {/* U aktivnom tabu prikazujemo samo trenutno stanje i potrošnju;
                          istorijsko stanje po opsegu datuma je dostupno u tabu "Arhiva". */}
                    </div>
                  )}
                </Card>
              </div>
              <div className="col-12 md:col-8 lg:col-9" />
            </div>

            <div className="mt-3">
              <div className="flex justify-content-end">
                <div className="text-600 flex align-items-center gap-2" style={{ fontSize: 12 }}>
                  <i className="pi pi-info-circle" />
                  <span>Prikazuje se aktivna potrošnja. Kartica za istoriju koristi i arhivirane podatke.</span>
                </div>
              </div>

              <h3 className="m-0 mt-2 mb-2">Lista potrošnje (aktivni zapisi)</h3>

              {rangeActive && filterFrom && filterTo && (
                <div className="flex justify-content-end text-600 mb-2" style={{ fontSize: 12 }}>
                  <i className="pi pi-filter mr-2" />
                  Filtriran period: {formatDate(filterFrom)} - {formatDate(filterTo)}
                </div>
              )}

              <DataTable
                value={filteredForTable}
                loading={loading}
                paginator
                rows={10}
                emptyMessage="Nema podataka"
                className="p-datatable-sm"
                sortField="date"
                sortOrder={-1}
                responsiveLayout="stack"
                breakpoint="768px"
              >
                <Column
                  field="date"
                  header="Datum"
                  sortable
                  body={(r: ConsumptionType) => formatDate(r.date)}
                />
                <Column field="shift" header="Smena" sortable body={(r: ConsumptionType) => shiftLabelShort(r.shift)} />
                <Column
                  field="time"
                  header="Vreme šarže"
                  body={(r: ConsumptionType) => (
                    <Button
                      type="button"
                      label={r.times && r.times.length > 0 ? `${r.times.length} vremena` : r.time || 'Detalji'}
                      text
                      size="small"
                      onClick={() => {
                        setTimeDetailsBase(r);
                        setTimeDetailsVisible(true);
                      }}
                    />
                  )}
                  sortable
                />
                <Column field="employeeName" header="Zaposleni" sortable />
                <Column field="resinName" header="Šarža" sortable />
                <Column field="materialName" header="Materijal" sortable />
                <Column field="usageCount" header="Broj korišćenja" sortable />
                <Column field="totalConsumption" header="Ukupna potrošnja (kg)" sortable body={(r: ConsumptionType) => formatKgSmart(r.totalConsumption || 0)} />
                <Column header="Akcije" body={actionsBodyTemplate} style={{ width: '140px' }} />
              </DataTable>
            </div>
          </Card>

          <Dialog
        header={editingConsumption ? 'Izmeni potrošnju' : 'Dodaj potrošnju'}
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        style={{ width: '600px' }}
        draggable={false}
      >
        {error && <Message severity="error" text={error} className="mb-3" />}

        <div className="grid">
          <div className="col-12">
            <div className="field">
              <label htmlFor="date" className="block mb-2">Datum</label>
              <Calendar
                id="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.value as Date })}
                dateFormat="dd.mm.yy"
                showIcon
                className="w-full"
                appendTo="self"
              />
            </div>
          </div>

          <div className="col-12">
            <div className="field">
              <label htmlFor="shift" className="block mb-2">Smena</label>
              <Dropdown
                id="shift"
                value={formData.shift}
                options={shiftOptions}
                onChange={(e) => setFormData({ ...formData, shift: e.value })}
                className="w-full"
                appendTo="self"
              />
            </div>
          </div>

          <div className="col-12">
            <div className="field">
              <label htmlFor="resinId" className="block mb-2">Šarža</label>
              <Dropdown
                id="resinId"
                value={formData.resinId}
                options={resinOptions}
                onChange={(e) => setFormData({ ...formData, resinId: e.value })}
                className="w-full"
                placeholder="Odaberite šaržu"
                appendTo="self"
                disabled={!!editingConsumption}
              />
              {editingConsumption && (
                <small className="text-600">Promena šarže na postojećem zapisu je onemogućena.</small>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="field">
              <label htmlFor="time" className="block mb-2">Vreme šarže</label>
              <InputText
                id="time"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                className="w-full"
                placeholder="npr. 06:30 ili 06:30; 06:45; 07:00"
              />
              <small className="text-600">
                I smena: 06:00-14:00, II: 14:00-22:00, III: 22:00-06:00
              </small>
              <div className="mt-2 flex gap-2">
                <Button
                  type="button"
                  label="Dodaj vreme"
                  icon="pi pi-plus"
                  size="small"
                  onClick={() => {
                    const values = parseSemicolonTimes(formData.time);
                    if (values.length === 0) {
                      setError('Unesite vreme pre dodavanja.');
                      return;
                    }

                    for (const value of values) {
                      if (parseTimeToMinutes(value) === null) {
                        setError(`Vreme "${value}" mora biti u formatu HH:mm (npr. 06:30).`);
                        return;
                      }
                      if (!isTimeInShift(value, formData.shift)) {
                        setError(`Vreme "${value}" mora biti u okviru izabrane smene (I: 06:00-14:00, II: 14:00-22:00, III: 22:00-06:00).`);
                        return;
                      }
                    }

                    const uniqueIncoming = Array.from(new Set(values));
                    const duplicates = uniqueIncoming.filter((value) => formData.times.includes(value));
                    if (duplicates.length > 0) {
                      setError(`Vreme "${duplicates[0]}" je već dodato.`);
                      return;
                    }

                    const newTimes = [...formData.times, ...uniqueIncoming];
                    setError('');
                    setFormData({
                      ...formData,
                      times: newTimes,
                      time: '',
                      usageCount: newTimes.length
                    });
                  }}
                />
              </div>
              {formData.times.length > 0 && (
                <div className="mt-2 flex gap-2 flex-wrap">
                  {formData.times.map((t) => (
                    <span
                      key={t}
                      className="px-2 py-1 border-round text-sm flex align-items-center gap-2"
                      style={{ background: '#e0f2fe', color: '#0369a1' }}
                    >
                      {t}
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => {
                            const newTimes = prev.times.filter((x) => x !== t);
                            return {
                              ...prev,
                              times: newTimes,
                              usageCount: newTimes.length || 1
                            };
                          })
                        }
                        aria-label={`Ukloni vreme ${t}`}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#0369a1',
                          padding: 0,
                          width: 14,
                          height: 14,
                          lineHeight: '14px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <i className="pi pi-times" style={{ fontSize: 10 }} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="field">
              <label htmlFor="employeeName" className="block mb-2">Zaposleni</label>
              <InputText
                id="employeeName"
                value={formData.employeeName}
                onChange={(e) => setFormData({ ...formData, employeeName: e.target.value })}
                className="w-full"
                placeholder="Unesite ime i prezime"
              />
            </div>
          </div>

          <div className="col-12">
            <div className="field">
              <label htmlFor="usageCount" className="block mb-2">
                Broj korišćenja
              </label>
              <InputNumber
                inputId="usageCount"
                value={formData.usageCount}
                onValueChange={() => {}}
                className="w-full"
                min={0}
                disabled
              />
              <small className="text-600">
                Automatski: broj unetih vremena šarže ({formData.times.length})
              </small>
            </div>
          </div>

          {!editingConsumption && (
            <div className="col-12">
              <div className="field">
                <Button
                  type="button"
                  label="Dodaj u listu"
                  icon="pi pi-plus"
                  severity="success"
                  onClick={() => {
                    // Dodavanje jednog zapisa (šarža + vremena + zaposleni) u lokalnu listu pre slanja u backend.
                    if (!formData.employeeName.trim()) {
                      setError('Zaposleni je obavezan pre dodavanja u listu.');
                      return;
                    }
                    if (!formData.resinId) {
                      setError('Izaberite šaržu za ovaj zapis.');
                      return;
                    }
                    if (!formData.times || formData.times.length === 0) {
                      setError('Potrebno je da dodate bar jedno vreme šarže pre dodavanja u listu.');
                      return;
                    }

                    setBatchEntries((prev) => [
                      ...prev,
                      {
                        resinId: formData.resinId,
                        employeeName: formData.employeeName.trim(),
                        times: formData.times
                      }
                    ]);

                    // Resetuj polja za sledeći zapis.
                    setFormData((prev) => ({
                      ...prev,
                      time: '',
                      times: [],
                      resinId: '',
                      employeeName: '',
                      usageCount: 1
                    }));
                    setError('');
                  }}
                />
                <small className="block mt-2 text-600">
                  Za svaki zapis izaberite šaržu, unesite vremena i zaposlenog, pa ga dodajte u listu. Možete uneti više različitih šarži u istoj smeni.
                </small>
              </div>
            </div>
          )}

          {!editingConsumption && batchEntries.length > 0 && (
            <div className="col-12">
              <div className="field">
                <label className="block mb-2">Zapisi koji će biti sačuvani</label>
                <div className="flex flex-column gap-3">
                  {batchEntries.map((entry, index) => {
                    const resin = resins.find((r) => r._id === entry.resinId);
                    const usageCount = entry.times.length;
                    return (
                      <div
                        key={index}
                        className="flex justify-content-between align-items-center px-3 py-2 border-1 border-round-lg surface-100 shadow-1"
                      >
                        <div className="flex flex-column">
                          <div className="flex align-items-center gap-2 mb-1">
                            <span className="font-semibold">
                              {resin ? resin.name : 'Nepoznata šarža'}
                            </span>
                            <span className="text-600" style={{ fontSize: 12 }}>
                              ({usageCount}x)
                            </span>
                          </div>
                          <div className="text-600" style={{ fontSize: 12 }}>
                            <span className="font-semibold mr-1">Zaposleni:</span>
                            <span>{entry.employeeName}</span>
                          </div>
                          <div className="text-600" style={{ fontSize: 12 }}>
                            <span className="font-semibold mr-1">Vremena:</span>
                            <span>{entry.times.join(', ')}</span>
                          </div>
                        </div>
                        <Button
                          type="button"
                          icon="pi pi-trash"
                          severity="danger"
                          rounded
                          text
                          onClick={() =>
                            setBatchEntries((prev) => prev.filter((_, i) => i !== index))
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="col-12 flex justify-content-end gap-2">
            <Button label={editingConsumption ? 'Sačuvaj' : 'Dodaj'} onClick={handleSubmit} severity="info" />
            <Button label="Otkaži" onClick={() => setModalVisible(false)} className="p-button-outlined" />
          </div>
        </div>
      </Dialog>

      {/* View more popup za vremena / zapise po šarži */}
        </TabPanel>

        <TabPanel header="Arhiva">
          <Card>
            <div className="grid mt-3">
              <div className="col-12 md:col-4 lg:col-3">
                <Card className="h-full">
                  {!selectedInv ? (
                    <div className="text-600">Izaberite materijal</div>
                  ) : (
                    <div>
                      <div className="text-center" style={{ fontWeight: 700, fontSize: 18 }}>
                        {selectedInv.name}
                      </div>

                      <div className="text-center text-600 mt-3">Istorijsko stanje (opseg datuma)</div>
                      <div className="flex flex-column gap-2 align-items-center mt-2">
                        <div className="flex align-items-center gap-2">
                          <Calendar
                            value={historyRange}
                            onChange={(e) => setHistoryRange((e.value as Date[]) || null)}
                            dateFormat="dd.mm.yy"
                            showIcon
                            selectionMode="range"
                            placeholder="Izaberite opseg datuma"
                            appendTo="self"
                          />
                          {historyRange && historyRange.length > 0 && (
                            <Button
                              type="button"
                              icon="pi pi-times"
                              className="p-button-text p-button-rounded"
                              aria-label="Poništi opseg"
                              onClick={() => setHistoryRange(null)}
                            />
                          )}
                        </div>
                        {historyRange && historySnapshots && (
                          <>
                            <div className="text-center text-600" style={{ fontSize: 12 }}>
                              Stanje do početka dana {formatDate(historySnapshots.startDate)}:
                            </div>
                            <div className="text-center" style={{ fontWeight: 700, fontSize: 16 }}>
                              {formatKgSmart(historySnapshots.availableStart)} {selectedInv.unit}
                            </div>
                            <div className="text-center text-600 mt-2" style={{ fontSize: 12 }}>
                              Stanje do početka dana {formatDate(historySnapshots.endDate)}:
                            </div>
                            <div className="text-center" style={{ fontWeight: 800, fontSize: 20 }}>
                              {formatKgSmart(historySnapshots.availableEnd)} {selectedInv.unit}
                            </div>
                            <div className="text-center text-600 mt-1" style={{ fontSize: 12 }}>
                              Promena u opsegu: {formatKgSmart(historySnapshots.delta)} {selectedInv.unit}
                            </div>
                          </>
                        )}
                        {historyRange && !historySnapshots && (
                          <div className="text-center text-600" style={{ fontSize: 12 }}>
                            Nema dovoljno podataka za izračunavanje stanja za izabrani opseg.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              </div>
              <div className="col-12 md:col-8 lg:col-9" />
            </div>

            <div className="mt-3">
              <div className="flex justify-content-end">
                <div className="text-600 flex align-items-center gap-2" style={{ fontSize: 12 }}>
                  <i className="pi pi-info-circle" />
                  <span>Prikazuje se arhivirana potrošnja. Akcije izmene nisu dozvoljene.</span>
                </div>
              </div>

              <h3 className="m-0 mt-2 mb-2">Lista potrošnje (arhiva)</h3>

              {rangeActive && filterFrom && filterTo && (
                <div className="flex justify-content-end text-600 mb-2" style={{ fontSize: 12 }}>
                  <i className="pi pi-filter mr-2" />
                  Filtriran period: {formatDate(filterFrom)} - {formatDate(filterTo)}
                </div>
              )}

              <DataTable
                value={filteredArchivedForTable}
                paginator
                rows={10}
                emptyMessage="Nema arhiviranih podataka"
                className="p-datatable-sm"
                sortField="date"
                sortOrder={-1}
                responsiveLayout="stack"
                breakpoint="768px"
              >
                <Column
                  field="date"
                  header="Datum"
                  sortable
                  body={(r: ConsumptionType) => formatDate(r.date)}
                />
                <Column field="shift" header="Smena" sortable body={(r: ConsumptionType) => shiftLabelShort(r.shift)} />
                <Column
                  field="time"
                  header="Vreme šarže"
                  body={(r: ConsumptionType) => (
                    <Button
                      type="button"
                      label={r.times && r.times.length > 0 ? `${r.times.length} vremena` : r.time || 'Detalji'}
                      text
                      size="small"
                      onClick={() => {
                        setTimeDetailsBase(r);
                        setTimeDetailsVisible(true);
                      }}
                    />
                  )}
                  sortable
                />
                <Column field="employeeName" header="Zaposleni" sortable />
                <Column field="resinName" header="Šarža" sortable />
                <Column field="materialName" header="Materijal" sortable />
                <Column field="usageCount" header="Broj korišćenja" sortable />
                <Column field="totalConsumption" header="Ukupna potrošnja (kg)" sortable body={(r: ConsumptionType) => formatKgSmart(r.totalConsumption || 0)} />
              </DataTable>
            </div>
          </Card>
        </TabPanel>
      </TabView>

      <Dialog
        header={
          timeDetailsBase
            ? `Detalji potrošnje - ${timeDetailsBase.resinName} (${formatDate(timeDetailsBase.date)})`
            : 'Detalji potrošnje'
        }
        visible={timeDetailsVisible}
        style={{ width: '600px' }}
        onHide={() => setTimeDetailsVisible(false)}
        draggable={false}
      >
        {timeDetailsBase && (
          <div className="flex flex-column gap-3">
            <div className="text-600" style={{ fontSize: 13 }}>
              <div>
                <span className="font-semibold mr-1">Šarža:</span>
                <span>{timeDetailsBase.resinName}</span>
              </div>
              <div>
                <span className="font-semibold mr-1">Smena:</span>
                <span>{shiftLabelShort(timeDetailsBase.shift)}</span>
              </div>
            </div>

            <div className="flex flex-column gap-2">
              {timeDetailsItems.map((item) => (
                <div
                  key={item._id}
                  className="px-3 py-2 border-1 border-round-lg surface-100 flex flex-column gap-1"
                >
                  <div className="flex justify-content-between align-items-center">
                    <div className="font-semibold" style={{ fontSize: 13 }}>
                      {item.employeeName || 'Bez imena'}
                    </div>
                    <div className="text-600" style={{ fontSize: 12 }}>
                      {item.usageCount || 0}x, {formatKgSmart(item.totalConsumption || 0)} kg
                    </div>
                  </div>
                  <div className="text-600" style={{ fontSize: 12 }}>
                    <span className="font-semibold mr-1">Vremena:</span>
                    <span>
                      {(item.times && item.times.length > 0
                        ? item.times
                        : item.time
                        ? [item.time]
                        : []
                      ).join(', ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-2 pt-2 border-top-1 surface-border flex justify-content-between text-700" style={{ fontSize: 13 }}>
              <div>
                <span className="font-semibold mr-1">Ukupno korišćenja (po šarži):</span>
                <span>{timeDetailsTotals.totalUsage}</span>
              </div>
              <div>
                <span className="font-semibold mr-1">Ukupna potrošnja (po šarži):</span>
                <span>{formatKgSmart(timeDetailsTotals.totalKg)} kg</span>
              </div>
            </div>
          </div>
        )}
      </Dialog>

      <ConfirmDialog />

      <Dialog
        header="Obriši sve podatke logistike"
        visible={bulkClearStep > 0}
        modal
        style={{ width: '520px', maxWidth: '95vw' }}
        onHide={() => {
          if (bulkClearLoading) return;
          setBulkClearStep(0);
          setBulkConfirmText('');
        }}
        closable={!bulkClearLoading}
      >
        {bulkClearStep === 1 && (
          <div>
            <div className="mb-3">
              <i
                className="pi pi-exclamation-triangle text-orange-500 mr-2"
                style={{ fontSize: 20, verticalAlign: 'middle' }}
              />
              <span className="font-semibold">Ova akcija je nepovratna.</span>
            </div>
            <p className="mt-0">
              Iz baze će biti trajno obrisani <strong>svi</strong> sledeći podaci za ovu sekciju:
            </p>
            <ul>
              <li>Sve potrošnje (aktivne i arhivirane)</li>
              <li>Sve prijemnice</li>
              <li>Sva rashodovanja</li>
            </ul>
            <p className="text-600" style={{ fontSize: 13 }}>
              Stanje materijala (težine) se ne diraju, ali se brojači potrošnje resetuju na 0.
              Materijali se ne brišu.
            </p>
            <div className="flex justify-content-end gap-2 mt-4">
              <Button
                label="Odustani"
                severity="secondary"
                onClick={() => {
                  setBulkClearStep(0);
                  setBulkConfirmText('');
                }}
              />
              <Button
                label="Nastavi"
                severity="danger"
                onClick={() => setBulkClearStep(2)}
              />
            </div>
          </div>
        )}

        {bulkClearStep === 2 && (
          <div>
            <p className="mt-0">
              Da bi se potvrdilo brisanje, ukucaj <strong>OBRIŠI SVE</strong> u polje ispod:
            </p>
            <InputText
              value={bulkConfirmText}
              onChange={(e) => setBulkConfirmText(e.target.value)}
              placeholder="OBRIŠI SVE"
              className="w-full"
              autoFocus
              disabled={bulkClearLoading}
            />
            <div className="flex justify-content-end gap-2 mt-4">
              <Button
                label="Nazad"
                severity="secondary"
                onClick={() => setBulkClearStep(1)}
                disabled={bulkClearLoading}
              />
              <Button
                label="Obriši trajno"
                icon="pi pi-trash"
                severity="danger"
                disabled={bulkConfirmText.trim() !== 'OBRIŠI SVE' || bulkClearLoading}
                loading={bulkClearLoading}
                onClick={async () => {
                  try {
                    setBulkClearLoading(true);
                    setError('');
                    const result = await logisticsBulkAPI.clearAll();
                    setBulkClearStep(0);
                    setBulkConfirmText('');
                    await fetchData();
                    confirmDialog({
                      message: `Obrisano: ${result.deleted.consumption} potrošnja, ${result.deleted.receipts} prijemnica, ${result.deleted.disposals} rashodovanja.`,
                      header: 'Brisanje završeno',
                      icon: 'pi pi-check',
                      acceptLabel: 'OK',
                      rejectClassName: 'hidden',
                      acceptClassName: 'p-button-success'
                    });
                  } catch (e: any) {
                    console.error('Bulk clear error:', e);
                    setError(
                      e?.response?.data?.message ||
                        'Greška pri brisanju podataka logistike.'
                    );
                  } finally {
                    setBulkClearLoading(false);
                  }
                }}
              />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default Consumption;
